import React from 'react';
import { Button, makeStyles, mergeClasses, shorthands, tokens, Tooltip } from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as EditComponent } from '../../../assets/Edit.svg';
import ProGPTIcon from '../../../assets/progpt-grph-0524-icon-rgb.png';
import { customColors, customFonts } from '../../../styles';
import { useAppSelector } from '../../../redux/hooks';
import { UserRole } from '../../../domain/users/models/User';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        ...shorthands.gap(tokens.spacingVerticalS),
        ...shorthands.padding('10px', tokens.spacingHorizontalM),
    },
    rootCollapsed: {
        alignItems: 'center',
        ...shorthands.padding(tokens.spacingVerticalXS, '0px'),
    },
    button: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: customFonts.Lato,
        fontWeight: 700,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
        ...shorthands.padding('10px', '12px'),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
    },
    buttonCollapsed: {
        minWidth: '48px',
        width: '48px',
        height: '48px',
        ...shorthands.padding('0px'),
        ...shorthands.borderRadius('4px'),
    },
    newChatButton: {
        backgroundColor: customColors.protivitiOrange,
        color: customColors.white,
        ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
        ':hover': {
            backgroundColor: customColors.white,
            color: customColors.summarizeButtonDarker,
            ...shorthands.borderColor(customColors.protivitiOrange),
        },
        ':disabled': {
            backgroundColor: tokens.colorNeutralBackground3,
            color: tokens.colorNeutralForegroundDisabled,
            ...shorthands.borderColor(tokens.colorNeutralStroke2),
        },
    },
    exploreAgentsButton: {
        backgroundColor: tokens.colorNeutralBackground2,
        color: customColors.darkNavy,
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground3,
            ...shorthands.borderColor(tokens.colorNeutralStrokeAccessible),
        },
    },
    exploreAgentsButtonCollapsed: {
        backgroundColor: customColors.white,
        ...shorthands.border('1px', 'solid', customColors.white),
        ':hover': {
            ...shorthands.borderColor(customColors.protivitiOrange),
        },
    },
    buttonContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
    exploreIcon: {
        width: '20px',
        height: '20px',
        objectFit: 'contain',
    },
    newChatIcon: {
        ...shorthands.transition('color', '0.2s', 'ease'),
        '& > svg': {
            width: '20px',
            height: '20px',
        },
    },
});

interface ChatListHeaderProps {
    onCreateChat: () => Promise<void>;
    isCollapsed?: boolean;
    isNewChatDisabled?: boolean;
}

const ChatListHeader: React.FC<ChatListHeaderProps> = ({
    onCreateChat,
    isCollapsed = false,
    isNewChatDisabled = false,
}) => {
    const styles = useStyles();
    const navigate = useNavigate();
    const activeUserInfo = useAppSelector((state) => state.app.activeUserInfo);

    const newChatTooltip = isNewChatDisabled
        ? 'Max active chat sessions reached. Please delete a chat to create a new one.'
        : 'New Chat';

    if (isCollapsed) {
        return (
            <div className={mergeClasses(styles.root, styles.rootCollapsed)}>
                <Tooltip content={newChatTooltip} relationship="label" positioning="after">
                    <span>
                        <Button
                            className={mergeClasses(styles.button, styles.buttonCollapsed, styles.newChatButton)}
                            onClick={() => void onCreateChat()}
                            disabled={isNewChatDisabled}
                        >
                            <span className={styles.newChatIcon}>
                                <EditComponent />
                            </span>
                        </Button>
                    </span>
                </Tooltip>
                {activeUserInfo?.role !== UserRole.RestrictedAccess && 
                    <Tooltip content="Explore Agents" relationship="label" positioning="after">
                        <Button
                            className={mergeClasses(
                                styles.button,
                                styles.buttonCollapsed,
                                styles.exploreAgentsButton,
                                styles.exploreAgentsButtonCollapsed,
                            )}
                            onClick={() => navigate('/explore-agents')}
                        >
                            <img src={ProGPTIcon} alt="" className={styles.exploreIcon} />
                        </Button>
                    </Tooltip>
                }
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <Tooltip content={newChatTooltip} relationship="label">
                <span>
                    <Button
                        className={mergeClasses(styles.button, styles.newChatButton)}
                        onClick={() => void onCreateChat()}
                        disabled={isNewChatDisabled}
                    >
                        <div className={styles.buttonContent}>
                            <span className={styles.newChatIcon}>
                                <EditComponent />
                            </span>
                            <span>New Chat</span>
                        </div>
                    </Button>
                </span>
            </Tooltip>
            {activeUserInfo?.role !== UserRole.RestrictedAccess &&
                <Button
                    className={mergeClasses(styles.button, styles.exploreAgentsButton)}
                    onClick={() => navigate('/explore-agents')}
                >
                    <div className={styles.buttonContent}>
                        <img src={ProGPTIcon} alt="Explore Agents" className={styles.exploreIcon} />
                        <span>Explore Agents</span>
                    </div>
                </Button>
            }
            
        </div>
    );
};

export default ChatListHeader;
